import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { FlaskConical, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import WhatsAppSendRecipientPicker, {
  type RecipientSourceTab,
} from './WhatsAppSendRecipientPicker';
import { devicePreviewFromTemplate, templateHeaderMimeType } from './whatsappTemplatePreview';
import {
  buildPreviewMessage,
  buildWhatsAppAttachmentMediaKey,
  formatFileSize,
  getAttachmentLimit,
  headerFormatAcceptAttribute,
} from './whatsappPreview';
import {
  applyDoctorHonorificDedupeToBodyValues,
  deduplicateDoctorPrefix,
} from './whatsappDoctorHonorific';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import type {
  WhatsAppDeliveryLog,
  WhatsAppNotificationEvent,
  WhatsAppSendRecipient,
  WhatsAppTestSendContext,
  WhatsAppTestSendMode,
  WhatsAppTestSendResult,
} from '../../models/MasterAdmin/whatsappHub';
import { WHATSAPP_BASE_PATH } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppCampaign, WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import { isApprovedWhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import {
  fetchApprovedWhatsAppTemplates,
  fetchRecentWhatsAppTestSends,
  fetchWhatsAppCampaigns,
  fetchWhatsAppTestAnalytics,
  fetchWhatsAppTestSendContext,
  fetchWhatsAppTestSendStatus,
  sendWhatsAppTestMessage,
  fetchRcsTestStatus,
  fetchRcsLogs,
  sendRcsTestMessage,
} from '../../services/MasterAdmin/whatsappCampaignService';
import { fetchWhatsAppNotificationCatalog } from '../../services/MasterAdmin/whatsappHubService';
import { fetchPublicMediaByKey, uploadMediaAsset } from '../../services/MasterAdmin/mediaAssetService';
import { formatDisplayPhone } from './whatsappUi';
import WhatsAppChannelToggle, { type MessageChannel } from './WhatsAppChannelToggle';
import type { RcsDeliveryLog } from '../../models/MasterAdmin/whatsappHub';

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800';

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  read: 'bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366]',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

function statusBadgeClass(status: string) {
  return STATUS_COLORS[String(status).toLowerCase()] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';
}

type RecipientEntryMode = 'phone' | 'pick';

const WhatsAppTestingPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<WhatsAppTestSendMode>('notification');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [notifications, setNotifications] = useState<WhatsAppNotificationEvent[]>([]);
  const [campaigns, setCampaigns] = useState<WhatsAppCampaign[]>([]);

  const [templateName, setTemplateName] = useState('');
  const [eventKey, setEventKey] = useState('');
  const [campaignId, setCampaignId] = useState('');

  const [recipientEntry, setRecipientEntry] = useState<RecipientEntryMode>('phone');
  const [pickedRecipients, setPickedRecipients] = useState<WhatsAppSendRecipient[]>([]);
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [recipientType, setRecipientType] = useState<'user' | 'vendor'>('user');
  const [recipientId, setRecipientId] = useState('');

  const [context, setContext] = useState<Record<string, string>>({});
  const [customBodyValues, setCustomBodyValues] = useState<Record<number, string>>({});
  const [testContext, setTestContext] = useState<WhatsAppTestSendContext | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string | null>(null);
  const [headerMimeType, setHeaderMimeType] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [buttonUrls, setButtonUrls] = useState<Record<string, string>>({});

  const [lastResult, setLastResult] = useState<WhatsAppTestSendResult | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<WhatsAppDeliveryLog | null>(null);
  const [recentSends, setRecentSends] = useState<WhatsAppDeliveryLog[]>([]);
  const [analytics, setAnalytics] = useState<Awaited<ReturnType<typeof fetchWhatsAppTestAnalytics>> | null>(null);

  const [rcsStatus, setRcsStatus] = useState<Awaited<ReturnType<typeof fetchRcsTestStatus>> | null>(null);
  const [channel, setChannel] = useState<MessageChannel>('whatsapp');
  const [rcsTemplateOverride, setRcsTemplateOverride] = useState('');
  const [rcsBodyValues, setRcsBodyValues] = useState<string[]>([]);
  const [rcsSending, setRcsSending] = useState(false);
  const [rcsLastResult, setRcsLastResult] = useState<Awaited<ReturnType<typeof sendRcsTestMessage>> | null>(null);
  const [rcsRecentLogs, setRcsRecentLogs] = useState<RcsDeliveryLog[]>([]);

  const enabledNotifications = useMemo(
    () =>
      notifications.filter(
        (n) => n.whatsappSupported && n.whatsappEnabled && Boolean(n.templateName)
      ),
    [notifications]
  );

  const selectedNotification = useMemo(
    () => enabledNotifications.find((n) => n.eventKey === eventKey) || null,
    [enabledNotifications, eventKey]
  );

  const selectedTemplate = useMemo(() => {
    const name = testContext?.templateName || templateName || selectedNotification?.templateName || '';
    return templates.find((t) => t.name === name) || null;
  }, [templates, testContext?.templateName, templateName, selectedNotification?.templateName]);

  const effectivePhone = useMemo(() => {
    if (recipientEntry === 'pick' && pickedRecipients[0]) {
      return {
        countryCode: pickedRecipients[0].countryCode || '+91',
        phoneNumber: String(pickedRecipients[0].phoneNumber || '').replace(/\D/g, '').slice(-10),
        contactName: pickedRecipients[0].displayName || '',
        recipientType: (pickedRecipients[0].recipientType === 'vendor' ? 'vendor' : 'user') as
          | 'user'
          | 'vendor',
        recipientId: pickedRecipients[0].recipientId || '',
      };
    }
    return {
      countryCode,
      phoneNumber: phoneNumber.replace(/\D/g, '').slice(-10),
      contactName,
      recipientType,
      recipientId,
    };
  }, [
    recipientEntry,
    pickedRecipients,
    countryCode,
    phoneNumber,
    contactName,
    recipientType,
    recipientId,
  ]);

  const previewVariableRows = useMemo(() => {
    if (lastResult?.resolvedBodyValues?.length) {
      return lastResult.resolvedBodyValues.map((value, index) => ({
        slot: index + 1,
        label: `Variable ${index + 1}`,
        source: 'resolved',
        contextKey: null as string | null,
        value: String(value ?? ''),
      }));
    }

    const mappings = testContext?.defaultVariableMappings || [];
    if (mappings.length) {
      return mappings.map((m, index) => {
        const slot = Number(m.slot) || index + 1;
        let value = customBodyValues[slot] != null ? customBodyValues[slot] : '';
        if (value === '') {
          if (m.sourceType === 'static') value = String(m.staticValue || '');
          else if (m.sourceType === 'deep_link') value = '[deep link]';
          else if (m.contextKey) value = String(context[m.contextKey] || '');
          if (!value && effectivePhone.contactName && /name/i.test(String(m.contextKey || m.label || ''))) {
            value = effectivePhone.contactName;
          }
        }
        return {
          slot,
          label: m.label || m.contextKey || `Variable ${slot}`,
          source: m.sourceType || 'context',
          contextKey: m.contextKey || null,
          value,
        };
      });
    }

    const count = testContext?.variableCount || selectedTemplate?.variableCount || 0;
    return Array.from({ length: count }, (_, index) => {
      const slot = index + 1;
      let value = customBodyValues[slot] != null ? customBodyValues[slot] : '';
      if (value === '' && effectivePhone.contactName && index === 0) {
        value = effectivePhone.contactName;
      }
      return {
        slot,
        label: `Variable ${slot}`,
        source: 'context',
        contextKey: null as string | null,
        value,
      };
    });
  }, [lastResult, testContext, selectedTemplate, context, customBodyValues, effectivePhone.contactName]);

  const previewBodyValues = useMemo(
    () => previewVariableRows.map((row) => row.value),
    [previewVariableRows]
  );

  const previewMessage = useMemo(() => {
    const templateBody = testContext?.bodyPreview || selectedTemplate?.bodyPreview || '';
    const slots = previewVariableRows.map((r) => ({ slot: r.slot, contextKey: r.contextKey }));
    return buildPreviewMessage(templateBody, previewBodyValues, slots);
  }, [testContext, selectedTemplate, previewVariableRows, previewBodyValues]);

  const sendRequirements = testContext?.sendRequirements;
  const requiresHeaderMedia = Boolean(sendRequirements?.requiresHeaderMedia);
  const urlButtons = sendRequirements?.buttons?.filter((b) => b.requiresUrl) || [];

  const devicePreviewProps = useMemo(() => {
    const base = selectedTemplate
      ? devicePreviewFromTemplate(selectedTemplate, {
          headerMediaUrl,
          headerMimeType: headerMimeType || templateHeaderMimeType(testContext?.headerFormat),
        })
      : {
          message: previewMessage || 'Select an enabled notification to preview',
          headerText: testContext?.headerText || null,
          footer: testContext?.footer || null,
          buttons: testContext?.buttons || [],
          headerMediaUrl,
          headerMimeType: headerMimeType || templateHeaderMimeType(testContext?.headerFormat),
        };
    return {
      ...base,
      message: previewMessage || base.message,
      headerText:
        testContext?.headerFormat === 'TEXT' ? testContext.headerText || base.headerText : base.headerText,
    };
  }, [selectedTemplate, previewMessage, testContext, headerMediaUrl, headerMimeType]);

  const pickerSources: RecipientSourceTab[] =
    effectivePhone.recipientType === 'vendor' ? ['vendors', 'contacts'] : ['users', 'contacts'];

  const resetTemplateExtras = useCallback(() => {
    setHeaderMediaUrl(null);
    setHeaderMimeType(null);
    setAttachmentName(null);
    setButtonUrls({});
  }, []);

  const handleAttachment = async (file: File | null) => {
    if (!file) return;
    const limit = getAttachmentLimit(file.type || '');
    if (file.size > limit) {
      toast.error(`File too large (max ${formatFileSize(limit)})`);
      return;
    }
    setUploadingAttachment(true);
    try {
      const key = buildWhatsAppAttachmentMediaKey(file);
      await uploadMediaAsset({ file, key, name: file.name });
      const pub = await fetchPublicMediaByKey(key);
      setHeaderMediaUrl(pub.fileUrl);
      setHeaderMimeType(pub.mimeType || file.type);
      setAttachmentName(file.name);
      toast.success('Header media ready');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Upload failed'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, notifRes, campRes] = await Promise.all([
        fetchApprovedWhatsAppTemplates(),
        fetchWhatsAppNotificationCatalog(),
        fetchWhatsAppCampaigns({ limit: 100 }),
      ]);
      setTemplates((tplRes.data || []).filter((t) => isApprovedWhatsAppTemplate(t)));
      const items = notifRes.data?.items || [];
      setNotifications(items.filter((n) => n.whatsappSupported));
      setCampaigns((campRes.data || []).filter((c) => c.templateName));

      const enabled = items.filter(
        (n) => n.whatsappSupported && n.whatsappEnabled && Boolean(n.templateName)
      );
      setEventKey((prev) => prev || enabled[0]?.eventKey || '');
      setMode('notification');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load test data'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecentAndAnalytics = useCallback(async () => {
    try {
      const [recent, stats] = await Promise.all([
        fetchRecentWhatsAppTestSends({ limit: 15 }),
        fetchWhatsAppTestAnalytics({ days: 30 }),
      ]);
      setRecentSends(recent);
      setAnalytics(stats);
    } catch {
      /* non-blocking */
    }
  }, []);

  const loadRcsStatus = useCallback(async () => {
    try {
      const data = await fetchRcsTestStatus();
      setRcsStatus(data);
      setRcsTemplateOverride((prev) => prev || data.templateName || data.templates?.[0]?.name || '');
    } catch {
      setRcsStatus(null);
    }
    try {
      const logs = await fetchRcsLogs({ limit: 15 });
      setRcsRecentLogs(logs.data || []);
    } catch {
      setRcsRecentLogs([]);
    }
  }, []);

  const handleRcsTest = async (dryRun: boolean) => {
    const pn = effectivePhone.phoneNumber;
    if (!pn || pn.length < 10) {
      toast.error('Choose a recipient or enter a valid 10-digit phone number');
      return;
    }
    if (!rcsTemplateOverride.trim() && !rcsStatus?.templateName) {
      toast.error('Enter an RCS template code name');
      return;
    }
    setRcsSending(true);
    try {
      const res = await sendRcsTestMessage({
        countryCode: effectivePhone.countryCode || '+91',
        phoneNumber: pn,
        contactName: effectivePhone.contactName || undefined,
        recipientType: effectivePhone.recipientType,
        recipientId: effectivePhone.recipientId || undefined,
        templateName: rcsTemplateOverride.trim() || undefined,
        bodyValues: rcsBodyValues,
        dryRun,
      });
      setRcsLastResult(res);
      if (dryRun) {
        toast.success(res.message || 'Dry-run OK — nothing sent');
      } else if (res.success && res.data?.sent) {
        toast.success(res.message || 'RCS test sent');
      } else {
        toast.error(res.message || res.data?.reason || 'RCS test failed');
      }
      void loadRcsStatus();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'RCS test failed'));
    } finally {
      setRcsSending(false);
    }
  };

  const loadTestContext = useCallback(async () => {
    const hasSelection =
      (mode === 'template' && templateName) ||
      (mode === 'notification' && eventKey) ||
      (mode === 'campaign' && campaignId);
    if (!hasSelection) {
      setTestContext(null);
      return;
    }

    setContextLoading(true);
    try {
      const data = await fetchWhatsAppTestSendContext({
        mode,
        templateName: mode === 'template' ? templateName : undefined,
        eventKey: mode === 'notification' ? eventKey : undefined,
        campaignId: mode === 'campaign' ? campaignId : undefined,
      });
      setTestContext(data);
      setContext(data.sampleContext || {});
      resetTemplateExtras();
      const initialButtons: Record<string, string> = {};
      for (const btn of data.sendRequirements?.buttons || []) {
        if (btn.requiresUrl && btn.urlTemplate && !btn.urlTemplate.includes('{{')) {
          initialButtons[String(btn.index)] = btn.urlTemplate;
        }
      }
      setButtonUrls(initialButtons);
      if (data.recipientType === 'vendor' || data.recipientType === 'user') {
        setRecipientType(data.recipientType);
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load notification template'));
      setTestContext(null);
    } finally {
      setContextLoading(false);
    }
  }, [mode, templateName, eventKey, campaignId, resetTemplateExtras]);

  useEffect(() => {
    void loadLists();
    void loadRecentAndAnalytics();
    void loadRcsStatus();
  }, [loadLists, loadRecentAndAnalytics, loadRcsStatus]);

  useEffect(() => {
    void loadTestContext();
  }, [loadTestContext]);

  useEffect(() => {
    if (!lastResult?.deliveryLogId) return undefined;
    const terminal = new Set(['delivered', 'read', 'failed']);
    let cancelled = false;

    const poll = async () => {
      try {
        const row = await fetchWhatsAppTestSendStatus(lastResult.deliveryLogId!);
        if (cancelled) return;
        setDeliveryStatus(row);
        if (terminal.has(String(row.status).toLowerCase())) {
          void loadRecentAndAnalytics();
        }
      } catch {
        /* ignore poll errors */
      }
    };

    void poll();
    const id = window.setInterval(() => {
      void poll();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [lastResult?.deliveryLogId, loadRecentAndAnalytics]);

  const handlePickRecipients = (next: WhatsAppSendRecipient[]) => {
    const one = next.slice(-1);
    setPickedRecipients(one);
    if (one[0]) {
      setCountryCode(one[0].countryCode || '+91');
      setPhoneNumber(String(one[0].phoneNumber || '').replace(/\D/g, '').slice(-10));
      setContactName(one[0].displayName || '');
      setRecipientType(one[0].recipientType === 'vendor' ? 'vendor' : 'user');
      setRecipientId(one[0].recipientId || '');
    }
  };

  const handleSend = async () => {
    const pn = effectivePhone.phoneNumber;
    if (!pn || pn.length < 10) {
      toast.error('Choose a recipient or enter a valid 10-digit phone number');
      return;
    }
    if (mode === 'notification' && !eventKey) {
      toast.error('Select an enabled notification');
      return;
    }
    if (mode === 'template' && !templateName) {
      toast.error('Select a template');
      return;
    }
    if (mode === 'campaign' && !campaignId) {
      toast.error('Select a campaign');
      return;
    }
    if (requiresHeaderMedia && !headerMediaUrl?.trim()) {
      toast.error(`This template requires a ${sendRequirements?.headerFormat || 'media'} header`);
      return;
    }
    for (const btn of urlButtons) {
      if (!buttonUrls[String(btn.index)]?.trim()) {
        toast.error(`Enter a URL for button "${btn.text}"`);
        return;
      }
    }

    setSending(true);
    setLastResult(null);
    setDeliveryStatus(null);
    try {
      const res = await sendWhatsAppTestMessage({
        mode,
        countryCode: effectivePhone.countryCode,
        phoneNumber: pn,
        contactName: effectivePhone.contactName || undefined,
        templateName: mode === 'template' ? templateName : undefined,
        eventKey: mode === 'notification' ? eventKey : undefined,
        campaignId: mode === 'campaign' ? campaignId : undefined,
        languageCode: testContext?.languageCode || 'en',
        recipientType: effectivePhone.recipientType,
        recipientId: effectivePhone.recipientId.trim() || undefined,
        context,
        headerMediaUrl: headerMediaUrl || undefined,
        headerMimeType: headerMimeType || undefined,
        headerFileName: attachmentName || undefined,
        buttonUrls,
      });
      if (!res.success) {
        toast.error(res.message || 'Test send failed');
        if (res.data) setLastResult(res.data);
        return;
      }
      setLastResult(res.data);
      toast.success(res.message || 'Test WhatsApp sent');
      void loadRecentAndAnalytics();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Test send failed'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#25D366]" />
      </div>
    );
  }

  return (
    <WhatsAppPanelShell
      title={channel === 'rcs' ? 'RCS testing' : 'WhatsApp testing'}
      description={
        channel === 'rcs'
          ? 'Pick a recipient, choose an Interakt RCS template, then dry-run or send a real RCS message.'
          : 'Pick an enabled notification, choose who to send to, preview template + variables, then send a real test.'
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <WhatsAppChannelToggle value={channel} onChange={setChannel} />
          <button
          type="button"
          onClick={() => {
            void loadLists();
            void loadRecentAndAnalytics();
            void loadTestContext();
            void loadRcsStatus();
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-600"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
        </div>
      }
    >
      {channel === 'rcs' ? (
      <section className="mb-6 rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-900/40 dark:bg-violet-950/20">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-violet-900 dark:text-violet-200">RCS test send</h3>
            <p className="mt-0.5 text-xs text-violet-800/80 dark:text-violet-300/80">
              Verify Interakt RCS before campaign retarget. Phone needs RCS (e.g. Google Messages).
            </p>
          </div>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              rcsStatus?.ready
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
            }`}
          >
            {rcsStatus?.ready ? 'Ready' : rcsStatus?.enabled ? 'Template missing' : 'Disabled'}
          </span>
        </div>

        {rcsStatus && (
          <p className="mb-3 text-xs text-violet-900/80 dark:text-violet-200/80">
            {rcsStatus.note}
            {rcsStatus.blockers?.length > 0 ? ` · Fix: ${rcsStatus.blockers.join(' · ')}` : ''}
            {rcsStatus.templateName ? ` · Default template: ${rcsStatus.templateName}` : ''}
          </p>
        )}

        {(rcsStatus?.templates || []).length > 0 && (
          <label className="mb-3 block text-sm">
            <span className="mb-1 block text-gray-600 dark:text-gray-300">RCS template</span>
            <select
              value={rcsTemplateOverride}
              onChange={(e) => {
                setRcsTemplateOverride(e.target.value);
                const tpl = (rcsStatus?.templates || []).find((t) => t.name === e.target.value);
                const count = Math.max(0, Number(tpl?.variableCount) || 0);
                setRcsBodyValues(Array.from({ length: count }, (_, i) => rcsBodyValues[i] || ''));
              }}
              className={inputClass}
            >
              <option value="">Select RCS template…</option>
              {(rcsStatus?.templates || []).map((t) => (
                <option key={t.name} value={t.name}>
                  {t.displayName || t.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-gray-600 dark:text-gray-300">RCS template code name</span>
            <input
              value={rcsTemplateOverride}
              onChange={(e) => setRcsTemplateOverride(e.target.value)}
              placeholder={rcsStatus?.templateName || 'your_rcs_template'}
              className={inputClass}
            />
          </label>
        </div>

        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Body variables</p>
            <button
              type="button"
              onClick={() => setRcsBodyValues((prev) => [...prev, ''])}
              className="text-xs text-violet-700 hover:underline"
            >
              Add variable
            </button>
          </div>
          {rcsBodyValues.map((value, index) => (
            <label key={`rcs-test-var-${index}`} className="mb-2 block text-sm">
              <span className="mb-1 block text-gray-600">{`{{${index + 1}}}`}</span>
              <div className="flex gap-2">
                <input
                  value={value}
                  onChange={(e) =>
                    setRcsBodyValues((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
                  }
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={() => setRcsBodyValues((prev) => prev.filter((_, i) => i !== index))}
                  className="text-xs text-gray-500"
                >
                  Remove
                </button>
              </div>
            </label>
          ))}
        </div>

        <section className="mb-4 rounded-xl border border-violet-200 bg-white/70 p-4 dark:border-violet-900/40 dark:bg-gray-900/40">
          <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">Recipient</h3>
          <p className="mb-3 text-xs text-gray-500">Enter a phone number or pick a user/vendor. Device must support RCS.</p>
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setRecipientEntry('phone')}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                recipientEntry === 'phone' ? 'bg-violet-600 text-white' : 'border border-gray-200 dark:border-gray-600'
              }`}
            >
              Enter number
            </button>
            <button
              type="button"
              onClick={() => setRecipientEntry('pick')}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                recipientEntry === 'pick' ? 'bg-violet-600 text-white' : 'border border-gray-200 dark:border-gray-600'
              }`}
            >
              Pick user / vendor
            </button>
          </div>
          {recipientEntry === 'phone' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block text-gray-600 dark:text-gray-300">Country code</span>
                <input value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className={inputClass} />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-gray-600 dark:text-gray-300">Phone number</span>
                <input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="9876543210"
                  className={inputClass}
                />
              </label>
            </div>
          ) : (
            <WhatsAppSendRecipientPicker
              singleSelect
              title="Pick one recipient"
              allowedSources={pickerSources}
              recipients={pickedRecipients}
              onChange={handlePickRecipients}
            />
          )}
        </section>

        <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={rcsSending}
              onClick={() => void handleRcsTest(true)}
              className="rounded-lg border border-violet-400 px-3 py-2 text-xs font-medium text-violet-800 disabled:opacity-60 dark:text-violet-200"
            >
              Dry run
            </button>
            <button
              type="button"
              disabled={rcsSending || !rcsStatus?.enabled}
              onClick={() => void handleRcsTest(false)}
              className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-60"
            >
              {rcsSending ? 'Sending…' : 'Send RCS test'}
            </button>
            {effectivePhone.phoneNumber ? (
              <span className="text-xs text-gray-500">
                To {formatDisplayPhone(effectivePhone.countryCode, effectivePhone.phoneNumber)}
              </span>
            ) : null}
        </div>

        {rcsLastResult && (
          <pre className="mt-3 max-h-40 overflow-auto rounded-lg bg-white/80 p-3 text-[11px] text-gray-700 dark:bg-gray-900/70 dark:text-gray-200">
            {JSON.stringify(rcsLastResult.data || rcsLastResult, null, 2)}
          </pre>
        )}

        {rcsRecentLogs.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Recent RCS sends</p>
            <table className="min-w-full text-left text-xs">
              <thead>
                <tr className="border-b border-violet-200">
                  <th className="py-2 pr-2">When</th>
                  <th className="py-2 pr-2">Template</th>
                  <th className="py-2 pr-2">Phone</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rcsRecentLogs.map((row) => (
                  <tr key={row.id} className="border-b border-violet-100 dark:border-gray-800">
                    <td className="whitespace-nowrap py-2 pr-2">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-2 font-mono">{row.rcsTemplateName || '—'}</td>
                    <td className="py-2 pr-2">{row.maskedPhone || row.phone}</td>
                    <td className="py-2">{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      ) : (
      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-5">
          {/* Step 1: notification */}
          <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-1 flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
              <FlaskConical className="h-4 w-4 text-[#25D366]" />
              1. Select enabled notification
            </h3>
            <p className="mb-3 text-xs text-gray-500">
              Only notifications turned ON in{' '}
              <Link to={`${WHATSAPP_BASE_PATH}/notifications`} className="text-[#128C7E] underline">
                Notifications
              </Link>{' '}
              appear here.
            </p>

            {mode === 'notification' ? (
              enabledNotifications.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
                  No enabled WhatsApp notifications found. Open Notifications, choose a template, turn WhatsApp
                  ON, save — then come back here.
                </div>
              ) : (
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">Notification</span>
                  <select
                    value={eventKey}
                    onChange={(e) => {
                      setEventKey(e.target.value);
                      setMode('notification');
                      setLastResult(null);
                    }}
                    className={inputClass}
                  >
                    <option value="">Select notification…</option>
                    {enabledNotifications.map((n) => (
                      <option key={n.eventKey} value={n.eventKey}>
                        {n.label} — {n.templateName}
                      </option>
                    ))}
                  </select>
                </label>
              )
            ) : (
              <p className="text-sm text-gray-500">
                Advanced mode is on ({mode}). Switch back to notification testing below if needed.
              </p>
            )}

            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="mt-3 text-xs font-medium text-gray-500 underline"
            >
              {showAdvanced ? 'Hide advanced options' : 'Advanced: test template / campaign'}
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-3 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-600">
                <div className="flex flex-wrap gap-2">
                  {(['notification', 'template', 'campaign'] as WhatsAppTestSendMode[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      className={`rounded-lg px-3 py-1.5 text-xs capitalize ${
                        mode === m
                          ? 'bg-[#25D366] text-white'
                          : 'border border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                {mode === 'template' && (
                  <select
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select template…</option>
                    {templates.map((t) => (
                      <option key={t.id || t.name} value={t.name}>
                        {t.displayName || t.name}
                      </option>
                    ))}
                  </select>
                )}
                {mode === 'campaign' && (
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select campaign…</option>
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.templateName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {contextLoading && (
              <p className="mt-2 text-sm text-gray-500">Loading notification template…</p>
            )}
          </section>

          {/* Step 2: recipient */}
          <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">2. Who should receive the test?</h3>
            <p className="mb-3 text-xs text-gray-500">Enter a phone number or pick a user/vendor.</p>

            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setRecipientEntry('phone')}
                className={`rounded-lg px-3 py-1.5 text-sm ${
                  recipientEntry === 'phone'
                    ? 'bg-[#25D366] text-white'
                    : 'border border-gray-200 dark:border-gray-600'
                }`}
              >
                Enter number
              </button>
              <button
                type="button"
                onClick={() => setRecipientEntry('pick')}
                className={`rounded-lg px-3 py-1.5 text-sm ${
                  recipientEntry === 'pick'
                    ? 'bg-[#25D366] text-white'
                    : 'border border-gray-200 dark:border-gray-600'
                }`}
              >
                Pick user / vendor
              </button>
            </div>

            {recipientEntry === 'phone' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">Country code</span>
                  <input value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className={inputClass} />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">Phone number</span>
                  <input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="9876543210"
                    className={inputClass}
                  />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">Display name (optional)</span>
                  <input
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Used for name variable preview"
                    className={inputClass}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">Recipient type</span>
                  <select
                    value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value as 'user' | 'vendor')}
                    className={inputClass}
                  >
                    <option value="user">User</option>
                    <option value="vendor">Vendor</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">User / vendor ID (optional)</span>
                  <input
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                    placeholder="Loads real profile data when available"
                    className={inputClass}
                  />
                </label>
              </div>
            ) : (
              <WhatsAppSendRecipientPicker
                singleSelect
                title="Pick one recipient"
                allowedSources={pickerSources}
                recipients={pickedRecipients}
                onChange={handlePickRecipients}
              />
            )}
          </section>

          {/* Template variable values / placeholders section */}
          {testContext && previewVariableRows.length > 0 && (
            <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">
                Template variable values ({previewVariableRows.map((r) => `{{${r.slot}}}`).join(', ')})
              </h3>
              <p className="mb-3 text-xs text-gray-500">
                Values to substitute into template placeholders. Edit directly to test custom values or doctor names.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {previewVariableRows.map((row) => (
                  <label key={row.slot} className="text-sm">
                    <span className="mb-1 block text-gray-600 dark:text-gray-300 font-medium">
                      Placeholder {'{{'}{row.slot}{'}}'} — {row.label}
                    </span>
                    <input
                      value={customBodyValues[row.slot] ?? row.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomBodyValues((prev) => ({ ...prev, [row.slot]: val }));
                        if (row.contextKey) {
                          setContext((prev) => ({ ...prev, [row.contextKey!]: val }));
                        }
                      }}
                      placeholder={`Value for {{${row.slot}}}`}
                      className={inputClass}
                    />
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Optional context / media */}
          {testContext && testContext.requiredContextKeys.length > 0 && (
            <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">Context (if needed)</h3>
              <p className="mb-3 text-xs text-gray-500">
                Sample values are loaded automatically. Replace with real IDs when the notification needs them.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {testContext.requiredContextKeys.map((key) => {
                  const field = testContext.availableContextFields?.find((f) => f.key === key);
                  return (
                    <label key={key} className="text-sm">
                      <span className="mb-1 block text-gray-600 dark:text-gray-300">
                        {field?.label || key}
                      </span>
                      <input
                        value={context[key] || ''}
                        onChange={(e) => setContext((prev) => ({ ...prev, [key]: e.target.value }))}
                        className={inputClass}
                      />
                    </label>
                  );
                })}
              </div>
            </section>
          )}

          {testContext && (requiresHeaderMedia || urlButtons.length > 0) && (
            <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Header & buttons</h3>
              {requiresHeaderMedia && (
                <div className="space-y-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-3 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800">
                    <PaperClipIcon className="h-4 w-4 shrink-0" />
                    {uploadingAttachment
                      ? 'Uploading…'
                      : attachmentName || 'Upload header media'}
                    <input
                      type="file"
                      accept={headerFormatAcceptAttribute(testContext.headerFormat)}
                      className="hidden"
                      disabled={uploadingAttachment}
                      onChange={(e) => void handleAttachment(e.target.files?.[0] || null)}
                    />
                  </label>
                  <input
                    value={headerMediaUrl || ''}
                    onChange={(e) => {
                      setHeaderMediaUrl(e.target.value.trim() || null);
                      if (e.target.value.trim()) setAttachmentName(null);
                    }}
                    placeholder="Or paste media URL"
                    className={inputClass}
                  />
                </div>
              )}
              {urlButtons.map((btn) => (
                <label key={btn.index} className="mt-3 block text-sm">
                  <span className="mb-1 block text-gray-600 dark:text-gray-300">
                    Button URL — {btn.text}
                  </span>
                  <input
                    value={buttonUrls[String(btn.index)] || ''}
                    onChange={(e) =>
                      setButtonUrls((prev) => ({ ...prev, [String(btn.index)]: e.target.value }))
                    }
                    className={inputClass}
                  />
                </label>
              ))}
            </section>
          )}

          {/* Step 3: preview summary */}
          <section className="rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-4 dark:border-[#25D366]/20">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">3. Preview before send</h3>
            {!testContext && !contextLoading ? (
              <p className="text-sm text-gray-500">Select an enabled notification to see template and variables.</p>
            ) : contextLoading ? (
              <p className="text-sm text-gray-500">Loading preview…</p>
            ) : (
              <div className="space-y-4 text-sm">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg bg-white/80 px-3 py-2 dark:bg-gray-900/60">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Template</p>
                    <p className="font-mono text-xs font-medium text-gray-900 dark:text-white">
                      {testContext?.templateName || '—'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {testContext?.messageCategory || '—'} · {testContext?.variableCount || 0} var(s)
                    </p>
                  </div>
                  <div className="rounded-lg bg-white/80 px-3 py-2 dark:bg-gray-900/60">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Recipient</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {effectivePhone.contactName || 'Test recipient'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {effectivePhone.phoneNumber
                        ? formatDisplayPhone(effectivePhone.countryCode, effectivePhone.phoneNumber)
                        : 'No number yet'}
                      {effectivePhone.recipientId ? ` · ${effectivePhone.recipientType}` : ''}
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                  <div className="border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800">
                    Variables & values
                  </div>
                  {previewVariableRows.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-gray-500">No template variables</p>
                  ) : (
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left">Slot</th>
                          <th className="px-3 py-2 text-left">Variable</th>
                          <th className="px-3 py-2 text-left">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewVariableRows.map((row) => (
                          <tr key={row.slot} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-3 py-2 font-mono">{`{{${row.slot}}}`}</td>
                            <td className="px-3 py-2">
                              <p className="font-medium">{row.label}</p>
                              <p className="text-[10px] text-gray-400">{row.source}</p>
                            </td>
                            <td className="max-w-[220px] truncate px-3 py-2" title={row.value}>
                              {row.value || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </section>

          <button
            type="button"
            disabled={
              sending ||
              contextLoading ||
              uploadingAttachment ||
              (mode === 'notification' && !eventKey) ||
              !effectivePhone.phoneNumber
            }
            onClick={() => void handleSend()}
            className="w-full rounded-xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {uploadingAttachment
              ? 'Uploading header media…'
              : sending
                ? 'Sending test WhatsApp…'
                : '4. Send test WhatsApp'}
          </button>

          {lastResult && (
            <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">Last test result</h3>
              <div className="space-y-1 text-sm">
                <p>
                  Status:{' '}
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
                      deliveryStatus?.status || 'sent'
                    )}`}
                  >
                    {deliveryStatus?.status || (lastResult.sent ? 'sent' : 'failed')}
                  </span>
                </p>
                <p>Phone: {lastResult.phone}</p>
                <p>Template: {lastResult.templateName}</p>
                {lastResult.resolvedBodyValues?.length ? (
                  <ul className="mt-2 list-inside list-disc text-xs text-gray-500">
                    {lastResult.resolvedBodyValues.map((v, i) => (
                      <li key={`${i}-${v}`}>
                        {`{{${i + 1}}}`}: {v}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {lastResult.reason && !lastResult.sent && (
                  <p className="text-red-600">{lastResult.reason}</p>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Message mockup
            </p>
            <WhatsAppDevicePreview
              {...devicePreviewProps}
              contactName={effectivePhone.contactName || 'Vedika.Health'}
            />
          </div>

          {selectedNotification && (
            <section className="rounded-xl border border-gray-200 p-4 text-sm dark:border-gray-700">
              <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">Selected notification</h3>
              <p className="font-medium">{selectedNotification.label}</p>
              <p className="text-xs text-gray-500">{selectedNotification.eventKey}</p>
              <p className="mt-2 font-mono text-xs">{selectedNotification.templateName}</p>
            </section>
          )}

          {analytics && (
            <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Test analytics (30 days)</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-500">Sent</p>
                  <p className="text-lg font-semibold">{analytics.totals.sent}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-500">Delivered</p>
                  <p className="text-lg font-semibold">{analytics.totals.delivered}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-500">Read</p>
                  <p className="text-lg font-semibold">{analytics.totals.read}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/60">
                  <p className="text-xs text-gray-500">Failed</p>
                  <p className="text-lg font-semibold">{analytics.totals.failed}</p>
                </div>
              </div>
            </section>
          )}

          <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Recent test sends</h3>
            {recentSends.length === 0 ? (
              <p className="text-sm text-gray-500">No test sends yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-2">When</th>
                      <th className="py-2 pr-2">Template</th>
                      <th className="py-2 pr-2">Phone</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSends.map((row) => (
                      <tr key={row.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="whitespace-nowrap py-2 pr-2">
                          {new Date(row.createdAt).toLocaleString()}
                        </td>
                        <td className="py-2 pr-2 font-mono">{row.templateName || '—'}</td>
                        <td className="py-2 pr-2">{row.maskedPhone}</td>
                        <td className="py-2">
                          <span className={`rounded px-1.5 py-0.5 ${statusBadgeClass(row.status)}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
      )}
    </WhatsAppPanelShell>
  );
};

export default WhatsAppTestingPanel;
